import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAllUserList } from '../../api/user';
import { getRoleList } from '../../api/role';
import { getDriverList } from "../../api/driver";
import { listUnread, listLatestMsg } from '../../api/message';
import { FormControl } from 'react-bootstrap';
import webSocket from '../../webSocket';
import { subscribeToMessages } from './messageBus';

/** * UserList component displays a list of users with their unread count.
 * It allows searching for users and updates the list in real-time as new messages are received.
 * @param {Object} props - The component props.
 * @param {Object} props.currentUser - The currently logged-in user.
 * @param {Object} props.currentUser.id - The currently logged-in user's ID.
 * @param {Object} props.currentUser.fullName - The currently logged-in user's full name.
 * @param {Function} props.onSelectUser - Callback function to handle user selection.
 * @param {string} props.selectedUserId - The ID of the currently selected user.
 * @param {string} props.initialSelectedUserId - The ID of the user to select from the URL.
 * @returns {JSX.Element} The rendered UserList component.
 */
const UserList = ({ currentUser, onSelectUser, selectedUserId, initialSelectedUserId }) => {

    const [filter, setFilter] = useState({});
    const [pagination, setPagination] = useState({ page: 1, pageSize: 999999 });
    const [userList, setUserList] = useState([]);
    const timeoutRef = useRef(null);
    const [menuOpenUser, setMenuOpenUser] = useState(null);

    const getData = useCallback(async (pagination, filter) => {
        const [userRes, driverRes, rolesRes, unreadMsg, latestMsg] = await Promise.all([
            getAllUserList({ skip: (pagination?.page - 1) * pagination?.pageSize, limit: pagination?.pageSize, ...filter }),
            getDriverList({ skip: 0, limit: 999999 }),
            getRoleList(),
            listUnread(),
            listLatestMsg(),
        ]);

        const tmpUserList = userRes.items.map(user => {
            const tmpRoles = [];

            try {
                user.roles.forEach(roleId => {
                    const role = rolesRes.find(r => r.id === roleId);
                    if (role) tmpRoles.push(role.name);
                });
            } catch (err) {
                console.error('Error mapping roles for user:', user, err);
            }

            let driverId = undefined;
            if (tmpRoles.includes('driver')) {
                const driver = driverRes.items.find(d => d.userId === user.id);
                if (driver) driverId = driver.id;
            }

            return {
                id: user.id,
                fullName: user.fullName,
                roles: tmpRoles,
                driverId: driverId,
                msgDate: latestMsg.filter(msg => msg.sender === user.id || msg.receiver === user.id)?.[0]?.createdAt,
                unreadCount: unreadMsg.filter(msg => msg.sender === user.id && msg.unread === true).length,
            }
        }).filter(user => user.id !== currentUser.id);

        tmpUserList.sort((a, b) => {
            if (!a.msgDate) return 1;
            if (!b.msgDate) return -1;
            return new Date(b.msgDate) - new Date(a.msgDate);
        });

        setUserList(tmpUserList);
    }, [currentUser.id]);

    useEffect(() => {
        const unsubscribe = subscribeToMessages((data) => {
            setUserList(prevList => {
                const newList = prevList.map(user => {
                    const isSender = user.id === data.sender;
                    const isReceiver = user.id === data.receiver && data.sender === currentUser.id;

                    if (isSender || isReceiver) {
                        const updatedUser = {
                            ...user,
                            msgDate: data.createdAt,
                        };

                        if (isSender && user.id !== selectedUserId) {
                            updatedUser.unreadCount = (user.unreadCount || 0) + 1;
                        }

                        return updatedUser;
                    }

                    return user;
                });

                newList.sort((a, b) => {
                    if (!a.msgDate) return 1;
                    if (!b.msgDate) return -1;
                    return new Date(b.msgDate) - new Date(a.msgDate)
                });

                return newList;
            });
        });

        return () => unsubscribe(); // Clean up on unmount
    });

    useEffect(() => {
        getData(pagination, filter)
    }, [pagination, filter, getData])

    useEffect(() => {
        if (!initialSelectedUserId || selectedUserId === initialSelectedUserId) return;

        const selectedUser = userList.find(user => user.id === initialSelectedUserId);
        if (!selectedUser) return;

        setUserList(prevList => prevList.map(user => (
            user.id === initialSelectedUserId ? { ...user, unreadCount: 0 } : user
        )));

        if (webSocket.socket && webSocket.socket.connected) {
            webSocket.send('readMessage', { sender: initialSelectedUserId });
        }

        onSelectUser(selectedUser);
    }, [initialSelectedUserId, onSelectUser, selectedUserId, userList]);

    const genUrl = (tab, user) => {
        if (user?.roles.includes('user')) {
            return `/user/detail/${user.id}#${tab}`;
        }

        if (user?.roles.includes('driver') && user.driverId) {
            return `/driver/detail/${user.driverId}#${tab}`;
        }
    }

    return (
        <div style={{ width: '25%', borderRight: '1px solid #ccc', height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            <FormControl
                id='search'
                type='text'
                placeholder='Search User'
                as="input"
                rows={1}
                style={{ margin: '10px', width: 'calc(100% - 20px)' }}
                onChange={(e) => {
                    const tmpValue = e.target.value;

                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    timeoutRef.current = setTimeout(() => {
                        setUserList([]);
                        setPagination({ ...pagination, page: 1 });
                        setFilter({ ...filter, search: tmpValue });
                    }, 1000);
                }}
            />
            <hr style={{ margin: 0 }}></hr>
            {userList.map((user) => (
                <div key={user.id} className="d-flex align-items-center justify-content-between p-1" role="button"
                    style={{
                        backgroundColor: selectedUserId === user.id ? '#f0f0f0' : 'white'
                    }}>
                    <div className="d-flex flex-fill align-items-center justify-content-between" onClick={() => {
                        if (selectedUserId === user.id) return; // Do not send readMessage if the user is already selected
                        setUserList(prevList => {
                            return prevList.map(u => {
                                if (u.id === user.id) {
                                    return { ...u, unreadCount: 0 }; // Reset unread count when selected
                                }
                                return u;
                            });
                        });
                        if (webSocket.socket && webSocket.socket.connected) {
                            webSocket.send('readMessage', { sender: user.id });
                        }
                        onSelectUser(user)
                    }}>
                        <div className="flex full">{user.fullName} ({user.roles.join(', ')})</div>
                        {user.unreadCount ? <div style={{
                            backgroundColor: 'red',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            color: 'white',
                            padding: '0px 5px',
                            borderRadius: '25%',
                        }}>{user.unreadCount}</div> : ''}
                    </div>

                    <div className="px-1" style={{ position: 'relative' }}>
                        <button
                            aria-label="more"
                            onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenUser(prev => (prev === user.id ? null : user.id));
                            }}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                padding: '4px 6px',
                                fontSize: '18px',
                            }}
                        >
                            &#8942;
                        </button>

                        {menuOpenUser === user.id && (
                            <div style={{
                                position: 'absolute',
                                right: 0,
                                top: '26px',
                                background: 'white',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                borderRadius: 4,
                                zIndex: 1000,
                                minWidth: 110,
                            }}>
                                <a
                                    role="menuitem"
                                    href={genUrl('wallet', user)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMenuOpenUser(null);
                                    }}
                                    style={{ display: 'block', padding: '8px 12px', cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}
                                >
                                    Wallet
                                </a>
                                <a
                                    role="menuitem"
                                    href={genUrl('order', user)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMenuOpenUser(null);
                                    }}
                                    style={{ display: 'block', padding: '8px 12px', cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}
                                >
                                    Details
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default UserList;
